// frontend/src/components/UserManagement/UserManagement.js

import React, { useState, useEffect, useContext } from 'react';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import './UserManagement.css';

function UserManagement() {
  const { user: currentUser } = useContext(UserContext);
  // Global admin ser alla kliniker och kan dela ut global admin.
  // Klinikadmin hanterar endast sin egen klinik.
  const isGlobalAdmin = currentUser?.is_admin;

  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [templates, setTemplates] = useState([]); // <-- NYTT
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    personal_number: '',
    clinic_id: '',
    is_admin: false,
    is_clinic_admin: false,
    default_journaltemplate_id: '', // <-- NYTT
    has_calendar: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editUserId, setEditUserId] = useState(null);

  useEffect(() => {
    fetchUsers();
    if (isGlobalAdmin) {
      fetchClinics();
    }
    fetchTemplates(); // <-- NYTT
  }, [isGlobalAdmin]);

  const fetchUsers = async () => {
    try {
      // Global admin hämtar även borttagna konton (för att kunna återställa).
      const url = isGlobalAdmin ? '/api/users?include_deleted=true' : '/api/users';
      const response = await apiClient.get(url);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchClinics = async () => {
    try {
      const response = await apiClient.get('/api/clinics');
      setClinics(response.data);
    } catch (error) {
      console.error('Error fetching clinics:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/api/journalmallar');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Stängs kalendern av för en användare med framtida bokningar: varna med
    // antal. Bokningarna finns kvar (visas nedtonade) men kolumnen försvinner.
    if (isEditing && !form.has_calendar) {
      const original = users.find((u) => u.user_id === editUserId);
      if (original?.has_calendar !== false) {
        try {
          const res = await apiClient.get(`/api/users/${editUserId}/future-appointments-count`);
          const count = res.data?.count || 0;
          if (count > 0 && !window.confirm(
            `Användaren har ${count} framtida bokning${count === 1 ? ' — den finns' : 'ar — de finns'} ` +
            `kvar men användaren får ingen kalenderkolumn. Fortsätta?`
          )) {
            return;
          }
        } catch (error) {
          console.error('Error fetching future appointments count:', error);
          alert('Kunde inte kontrollera framtida bokningar');
          return;
        }
      }
    }

    try {
      // Klinikadmin skapar alltid användare i sin egen klinik
      const payload = { ...form };
      if (!isGlobalAdmin) {
        payload.clinic_id = currentUser?.clinic_id;
        delete payload.is_admin;
      }

      if (isEditing) {
        if (!payload.password) {
          delete payload.password;
        }
        await apiClient.put(`/api/users/${editUserId}`, payload);
        alert('Användare uppdaterad');
      } else {
        await apiClient.post('/api/users', payload);
        alert('Användare skapad');
      }

      setForm({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        personal_number: '',
        clinic_id: '',
        is_admin: false,
        is_clinic_admin: false,
        default_journaltemplate_id: '', // <-- NYTT
        has_calendar: true,
      });
      setIsEditing(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Ett fel uppstod');
    }
  };

  const handleEdit = (user) => {
    setForm({
      username: user.username || '',
      email: user.email || '',
      password: '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      personal_number: user.personal_number || '',
      clinic_id: user.clinic_id || '',
      is_admin: user.is_admin || false,
      is_clinic_admin: user.is_clinic_admin || false,
      default_journaltemplate_id: user.default_journaltemplate_id || '', // <-- NYTT
      has_calendar: user.has_calendar !== false,
    });
    setIsEditing(true);
    setEditUserId(user.user_id);
  };

  const handleDelete = async (id) => {
    const target = users.find((u) => u.user_id === id);
    const name = target ? `${target.first_name} ${target.last_name}` : 'användaren';

    if (!window.confirm(
      `Ta bort ${name}?\n\n` +
      `Kontot döljs och kan inte längre logga in, men historik ` +
      `(bokningar, journaler) och namnet bevaras. En global administratör ` +
      `kan återställa kontot vid behov.`
    )) {
      return;
    }

    try {
      const response = await apiClient.delete(`/api/users/${id}`);
      alert(response.data?.msg || 'Användare borttagen');
      fetchUsers();
    } catch (error) {
      const data = error.response?.data;
      // 409 = framtida bokningar måste flyttas/avbokas först
      if (error.response?.status === 409 && data?.requires_action) {
        alert(data.msg);
      } else {
        console.error('Error deleting user:', error);
        alert(data?.msg || 'Ett fel uppstod vid borttagning');
      }
    }
  };

  const handleRestore = async (user) => {
    if (!window.confirm(
      `Återställ ${user.first_name} ${user.last_name}?\n\n` +
      `Kontot blir fryst och kan sedan aktiveras via Aktivera-knappen.`
    )) {
      return;
    }
    try {
      const response = await apiClient.post(`/api/users/${user.user_id}/restore`);
      alert(response.data?.msg || 'Användare återställd');
      fetchUsers();
    } catch (error) {
      console.error('Error restoring user:', error);
      alert(error.response?.data?.msg || 'Ett fel uppstod');
    }
  };

  const handleFreeze = async (user) => {
    const action = user.is_active ? 'frysa' : 'återaktivera';
    if (!window.confirm(`Vill du ${action} kontot för ${user.first_name} ${user.last_name}?`)) {
      return;
    }

    try {
      const endpoint = user.is_active ? 'freeze' : 'unfreeze';
      await apiClient.post(`/api/users/${user.user_id}/${endpoint}`);
      alert(user.is_active ? 'Kontot har frysets' : 'Kontot har återaktiverats');
      fetchUsers();
    } catch (error) {
      console.error('Error freezing/unfreezing user:', error);
      alert(error.response?.data?.msg || 'Ett fel uppstod');
    }
  };

  return (
    <div className="user-management-container">
      <h2>Användarhantering</h2>
      <form onSubmit={handleSubmit} className="user-form">
        <input
          type="text"
          name="first_name"
          placeholder="Förnamn"
          value={form.first_name}
          onChange={handleFormChange}
          required
        />
        <input
          type="text"
          name="last_name"
          placeholder="Efternamn"
          value={form.last_name}
          onChange={handleFormChange}
          required
        />
        <input
          type="text"
          name="personal_number"
          placeholder="Personnummer"
          value={form.personal_number}
          onChange={handleFormChange}
          required
        />
        <input
          type="text"
          name="username"
          placeholder="Användarnamn"
          value={form.username}
          onChange={handleFormChange}
          required
        />
        <input
          type="email"
          name="email"
          placeholder="E-post"
          value={form.email}
          onChange={handleFormChange}
          required
        />
        <input
          type="password"
          name="password"
          placeholder="Lösenord"
          value={form.password}
          onChange={handleFormChange}
          required={!isEditing}
        />

        {/* Klinikval - endast global admin. Klinikadmin skapar alltid i egen klinik. */}
        {isGlobalAdmin && (
          <select
            name="clinic_id"
            value={form.clinic_id}
            onChange={handleFormChange}
            required
          >
            <option value="">-- Välj Klinik --</option>
            {clinics.map((clinic) => (
              <option key={clinic.clinic_id} value={clinic.clinic_id}>
                {clinic.clinic_name}
              </option>
            ))}
          </select>
        )}

        <select
          name="default_journaltemplate_id"
          value={form.default_journaltemplate_id}
          onChange={handleFormChange}
        >
          <option value="">Ingen standardjournalmall (valfritt)</option>
          {templates.map((t) => (
            <option key={t.template_id} value={t.template_id}>
              {t.template_name}
            </option>
          ))}
        </select>

        {/* Har kalender: av för t.ex. adminkonton - ingen kalenderkolumn och
            inte valbar som behandlare. Samma behörighet som övriga fält. */}
        <label style={{ marginLeft: '10px' }} title="Av = ingen kalenderkolumn och inte valbar som behandlare">
          Har kalender (tar emot bokningar):
          <input
            type="checkbox"
            name="has_calendar"
            checked={form.has_calendar}
            onChange={handleFormChange}
            style={{ marginLeft: '5px' }}
          />
        </label>

        {/* Admin-roller (global + klinikadmin) delas endast ut av global admin.
            En klinikadmin hanterar bara vanliga användare. */}
        {isGlobalAdmin && (
          <>
            <label style={{ marginLeft: '10px' }}>
              Admin:
              <input
                type="checkbox"
                name="is_admin"
                checked={form.is_admin}
                onChange={handleFormChange}
                style={{ marginLeft: '5px' }}
              />
            </label>

            <label style={{ marginLeft: '10px' }}>
              Klinikadmin:
              <input
                type="checkbox"
                name="is_clinic_admin"
                checked={form.is_clinic_admin}
                onChange={handleFormChange}
                style={{ marginLeft: '5px' }}
              />
            </label>
          </>
        )}

        <button type="submit">
          {isEditing ? 'Uppdatera' : 'Skapa'}
        </button>
      </form>

      <table className="user-table">
        <thead>
          <tr>
            <th>Förnamn</th>
            <th>Efternamn</th>
            <th>Personnummer</th>
            <th>Användarnamn</th>
            <th>E-post</th>
            <th>Klinik-ID</th>
            <th>Admin</th>
            <th>Klinikadmin</th>
            <th>Kalender</th>
            <th>Status</th>
            <th>Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.user_id}
              className={u.is_deleted ? 'user-deleted' : !u.is_active ? 'user-frozen' : ''}
            >
              <td>{u.first_name}</td>
              <td>{u.last_name}</td>
              <td>{u.personal_number}</td>
              <td>{u.username}</td>
              <td>{u.email}</td>
              <td>{u.clinic_id}</td>
              <td>{u.is_admin ? 'Ja' : 'Nej'}</td>
              <td>{u.is_clinic_admin ? 'Ja' : 'Nej'}</td>
              <td>{u.has_calendar !== false ? 'Ja' : 'Nej'}</td>
              <td>
                <span className={`status-badge ${
                  u.is_deleted ? 'status-deleted' : u.is_active ? 'status-active' : 'status-frozen'
                }`}>
                  {u.is_deleted ? 'Borttagen' : u.is_active ? 'Aktiv' : 'Fryst'}
                </span>
              </td>
              <td>
                {u.is_deleted ? (
                  // Borttagna konton syns bara för global admin, som kan återställa
                  <button onClick={() => handleRestore(u)} className="btn-unfreeze">
                    Återställ
                  </button>
                ) : (isGlobalAdmin || (!u.is_admin && !u.is_clinic_admin)) ? (
                  /* Klinikadmin får bara hantera vanliga användare, inte andra admins */
                  <>
                    <button onClick={() => handleEdit(u)}>Redigera</button>
                    <button
                      onClick={() => handleFreeze(u)}
                      className={u.is_active ? 'btn-freeze' : 'btn-unfreeze'}
                    >
                      {u.is_active ? 'Frys' : 'Aktivera'}
                    </button>
                    <button onClick={() => handleDelete(u.user_id)} className="btn-delete">Ta Bort</button>
                  </>
                ) : (
                  <span className="user-managed-elsewhere">Hanteras av administratör</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UserManagement;
